import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { Sparkles, Mail, Lock, User, Phone, Building2, Target, GraduationCap, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const Auth = () => {
  const { login: authLogin } = useAuth();
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'signup'
  const [credentials, setCredentials] = useState({ 
    name: '', mobile: '', email: '', password: '', 
    branch: '', target_company: '', skill_level: 'Beginner', otp: ''
  });
  const [authLoading, setAuthLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const requestOTP = async () => {
    if (!credentials.email) {
      setAuthError('Please enter your email first to receive an OTP.');
      return;
    }
    setOtpLoading(true);
    setAuthError('');
    setAuthSuccess('');

    try {
      const response = await api.sendOTP(credentials.email);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to send OTP');
      
      setOtpSent(true);
      if (data.isDeveloperMode) {
        setAuthSuccess('Developer Mode: Your OTP has been logged to the BACKEND terminal. Please check there to continue.');
      } else {
        setAuthSuccess('OTP sent successfully! Please check your email inbox.');
      }
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const processGoogleLogin = async (credentialResponse) => {
    try {
      const response = await api.googleLogin(credentialResponse.credential);
      if (!response.ok) throw new Error('Google Sign-In Failed');
      const data = await response.json();
      finishLogin(data);
    } catch (error) {
      setAuthError(error.message);
    }
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');

    try {
      const payload = authMode === 'signup' 
        ? credentials 
        : { email: credentials.email, password: credentials.password };

      const response = authMode === 'signup' 
        ? await api.signup(payload)
        : await api.login(payload);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || (authMode === 'signup' ? 'Signup failed' : 'Login failed'));
      }

      const data = await response.json();
      finishLogin(data);
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const finishLogin = (data) => {
    authLogin(data.user, data.token);
    navigate('/');
  };

  return (
    <div className="flex items-center justify-center min-h-[80vh] py-8">
      <div className="w-full max-w-5xl grid lg:grid-cols-5 gap-0 rounded-3xl overflow-hidden shadow-2xl shadow-indigo-500/10 dark:shadow-black/30 animate-scale-in">
        
        {/* Left Branding Panel */}
        <div className="lg:col-span-2 relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 p-10 flex flex-col justify-center items-center text-center overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
            <div className="absolute w-48 h-48 rounded-full bg-white/10 -top-16 -left-16 animate-float-slow" />
            <div className="absolute w-32 h-32 rounded-2xl bg-white/10 bottom-10 -right-8 rotate-12 animate-float" />
            <div className="absolute w-20 h-20 rounded-full bg-white/5 top-1/2 left-1/3 animate-morph" />
            <div className="absolute inset-0 opacity-10"
              style={{
                backgroundImage: `radial-gradient(rgba(255,255,255,0.3) 1px, transparent 1px)`,
                backgroundSize: '24px 24px',
              }}
            />
          </div>

          <div className="relative z-10 space-y-6">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto shadow-lg animate-bounce-in">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-extrabold text-white font-display">PrepMaster</h2>
            <p className="text-white/70 text-sm leading-relaxed max-w-xs mx-auto">
              {authMode === 'signup' 
                ? 'Join thousands of students preparing smarter for campus placements.'
                : 'Welcome back! Your preparation progress awaits.'}
            </p>
            
            {/* Mini feature list */}
            <div className="space-y-3 text-left max-w-xs mx-auto pt-4">
              {[
                { icon: '🎯', text: '200+ DSA Problems' },
                { icon: '🧠', text: 'Aptitude Test Practice' },
                { icon: '📊', text: 'Real-time Progress' },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-white/80 text-sm">
                  <span className="text-base">{f.icon}</span>
                  <span>{f.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Form Panel */}
        <div className="lg:col-span-3 bg-white dark:bg-gray-900 p-8 md:p-10">
          {/* Tabs */}
          <div className="flex mb-8 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
            <button
              onClick={() => { setAuthMode('login'); setAuthError(''); }}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ${
                authMode === 'login' 
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
              }`}
            >
              Login
            </button>
            <button
              onClick={() => { setAuthMode('signup'); setAuthError(''); }}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ${
                authMode === 'signup' 
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' 
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Google Login */}
          <div className="flex justify-center overflow-hidden rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 mb-6 border border-gray-100 dark:border-gray-700">
            <GoogleLogin
              onSuccess={processGoogleLogin}
              onError={() => setAuthError('Google Login failed. Please try again.')}
              useOneTap
              shape="pill"
            />
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-gray-700" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-3 bg-white dark:bg-gray-900 text-gray-400 font-medium uppercase tracking-wider">
                Or continue with email
              </span>
            </div>
          </div>

          {/* Success/Error Messages */}
          {authError && (
            <div className="mb-5 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl px-4 py-3 font-medium text-center animate-slide-up-fade">
              {authError}
            </div>
          )}
          {authSuccess && (
            <div className="mb-5 text-sm text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 rounded-xl px-4 py-3 font-medium text-center animate-slide-up-fade">
              {authSuccess}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleAuthSubmit} className="space-y-4">
            {authMode === 'signup' && (
              <div className="grid grid-cols-2 gap-4 animate-fade-in-up">
                <InputField icon={<User />} id="name" name="name" type="text" required placeholder="Full Name" value={credentials.name} onChange={handleInputChange} />
                <InputField icon={<Phone />} id="mobile" name="mobile" type="tel" placeholder="Mobile (Optional)" value={credentials.mobile} onChange={handleInputChange} />
                <InputField icon={<GraduationCap />} id="branch" name="branch" type="text" placeholder="Branch / Major" value={credentials.branch} onChange={handleInputChange} />
                <InputField icon={<Building2 />} id="target_company" name="target_company" type="text" placeholder="Target Company" value={credentials.target_company} onChange={handleInputChange} />
                <div className="col-span-2">
                  <div className="relative">
                    <Target className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select 
                      id="skill_level" name="skill_level" value={credentials.skill_level} onChange={handleInputChange} 
                      className="input-field pl-10 appearance-none cursor-pointer"
                    >
                      <option value="Beginner">🌱 Beginner (Started Coding)</option>
                      <option value="Intermediate">⚡ Intermediate (Doing Leetcode)</option>
                      <option value="Advanced">🏆 Advanced (Contest Pro)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <div className="flex-1">
                <InputField icon={<Mail />} id="email" name="email" type="email" required placeholder="Email Address" value={credentials.email} onChange={handleInputChange} />
              </div>
              {authMode === 'signup' && (
                <button
                  type="button"
                  onClick={requestOTP}
                  disabled={otpLoading}
                  className="px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-bold rounded-xl border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 transition-colors flex items-center justify-center min-w-[100px]"
                >
                  {otpLoading ? <div className="w-4 h-4 border-2 border-indigo-600/30 border-t-indigo-600 rounded-full animate-spin" /> : (otpSent ? 'Resend' : 'Send OTP')}
                </button>
              )}
            </div>

            {authMode === 'signup' && otpSent && (
              <div className="animate-fade-in-up">
                <InputField 
                  icon={<Lock />} 
                  id="otp" 
                  name="otp" 
                  type="text" 
                  required 
                  placeholder="6-digit OTP code" 
                  maxLength={6} 
                  value={credentials.otp} 
                  onChange={handleInputChange} 
                />
              </div>
            )}
            
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
              <input 
                id="password" name="password" 
                type={showPassword ? 'text' : 'password'} 
                required minLength={4} 
                value={credentials.password} 
                onChange={handleInputChange} 
                className="input-field pl-10 pr-10" 
                placeholder="Password" 
              />
              <button 
                type="button"
                onClick={() => setShowPassword(!showPassword)} 
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors z-10"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <button 
              type="submit" 
              disabled={authLoading} 
              className="btn-primary w-full mt-2 flex items-center justify-center gap-2"
            >
              {authLoading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </span>
              ) : (
                <>
                  {authMode === 'signup' ? 'Create Account' : 'Login to Dashboard'}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Toggle auth mode */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {authMode === 'signup' ? 'Already have an account?' : 'New to PrepMaster?'}
              <button 
                type="button" 
                onClick={() => setAuthMode(authMode === 'signup' ? 'login' : 'signup')} 
                className="ml-1.5 font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
              >
                {authMode === 'signup' ? 'Login here' : 'Sign up free'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Reusable Input with Icon ─── */
const InputField = ({ icon, ...props }) => (
  <div className="relative">
    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
      {React.cloneElement(icon, { className: 'w-4 h-4' })}
    </span>
    <input {...props} className="input-field pl-10" />
  </div>
);

export default Auth;
